import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useTheme } from '@material-ui/core/styles';
import MdClose from '@meronex/icons/md/MdClose';
import { toast } from 'react-toastify';
import ICSV from '@meronex/icons/fi/FiFileText';
import ICheck from '@meronex/icons/md/MdCheckCircle';
import IError from '@meronex/icons/md/MdError';
import IDelete from '@meronex/icons/fi/FiTrash2';
import IDownload from '@meronex/icons/fi/FiDownload';
import IBookmark from '@meronex/icons/md/MdBookmark';
import { exportToExcel } from '../../../../utils/exportToExcel';
import { storesMng, getServerHttpUrl } from '../../api/storesMng';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import { App } from '@meronex/app';
import { createProgressTracker, callWithTabRetry } from '../../../../utils/progressTracker';
import { resolveUnitRate } from '../../api/pricing';

const { server } = App;

function GenerateReportsDialog(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const { open, onClose, store } = props;

  const [exportState, setExportState] = React.useState({
    value: 'idle',
    message: '',
  });
  const [selectedReport, setSelectedReport] = React.useState(null);
  const [useSavedValuation, setUseSavedValuation] = React.useState(false);
  const [saveValuation, setSaveValuation] = React.useState(false);
  const [valuationsExpanded, setValuationsExpanded] = React.useState(false);
  const now = new Date();
  const [includeInactive, setIncludeInactive] = React.useState(false);

  // Checkpoints (saved inventory valuations for faster future reports)
  const [checkpoints, setCheckpoints] = React.useState([]);
  const [checkpointsLoading, setCheckpointsLoading] = React.useState(false);
  const [editingValuationId, setEditingValuationId] = React.useState(null);
  const editNameRef = React.useRef('');
  const editNoteRef = React.useRef('');
  const [confirmAction, setConfirmAction] = React.useState(null); // { message, onConfirm }

  const loadCheckpoints = async () => {
    if (!store?._id) return;
    setCheckpointsLoading(true);
    try {
      const result = await storesMng.getInventorySnapshots(store._id);
      setCheckpoints(result || []);
    } catch (e) {
      console.error('Failed to load checkpoints:', e);
    }
    setCheckpointsLoading(false);
  };

  const handleClearCheckpoints = () => {
    if (!store?._id) return;
    setConfirmAction({
      message: 'Remove all saved valuations for this store?',
      onConfirm: async () => {
        try {
          const count = await storesMng.clearInventorySnapshots(store._id);
          setCheckpoints([]);
          toast.success(`Cleared ${count} valuation${count !== 1 ? 's' : ''}`);
        } catch (e) {
          toast.error('Failed to clear valuations');
        }
      },
    });
  };

  const handleDeleteCheckpoint = (snapshotId) => {
    setConfirmAction({
      message: 'Remove this saved valuation?',
      onConfirm: async () => {
        try {
          await storesMng.deleteInventorySnapshot(snapshotId);
          setCheckpoints(prev => prev.filter(c => c._id !== snapshotId));
          toast.success('Valuation removed');
        } catch (e) {
          toast.error('Failed to remove valuation');
        }
      },
    });
  };

  const handleExportCheckpoint = async (snapshotId) => {
    try {
      toast.info('Exporting saved valuation...');
      const response = await storesMng.exportInventorySnapshot(snapshotId);
      if (!response?.items?.length) {
        toast.info('No data in this valuation');
        return;
      }
      const data = response.items
        .filter(item => includeInactive || item.storeItem?.isActive !== false)
        .map((item) => ({
          'Name': item.itemType?.name || 'N/A',
          'Code': item.itemType?.code || 'N/A',
          'Unit': item.itemType?.unit || 'N/A',
          'Category': item.itemType?.category?.name || 'N/A',
          'Status': item.storeItem?.isActive === false ? 'Inactive' : 'Active',
          'Quantity': item.quantityAtDate || 0,
          'Weighted Avg Price': item.rateAtDate != null
            ? Number(item.rateAtDate).toFixed(2)
            : 'N/A',
          'Closing Value': item.closingValue != null
            ? Number(item.closingValue).toFixed(2)
            : 'N/A',
        }));
      const dateStr = new Date(response.snapshotDate).toISOString().split('T')[0];
      exportToExcel(data, `closing-inventory-${dateStr}.xlsx`);
      toast.success(`Exported ${data.length} items`);
    } catch (e) {
      console.error('Failed to export valuation:', e);
      toast.error('Failed to export valuation');
    }
  };

  const handleUpdateCheckpoint = async (snapshotId, name, note) => {
    try {
      await storesMng.updateInventorySnapshot({ snapshotId, name: name || null, note: note || null });
      setCheckpoints(prev => prev.map(c =>
        c._id === snapshotId ? { ...c, name: name || null, note: note || null } : c
      ));
      toast.success('Valuation updated');
    } catch (e) {
      toast.error('Failed to update valuation');
    }
  };

  // Load checkpoints when closing inventory is selected
  React.useEffect(() => {
    if (selectedReport === 'closingInventory') {
      loadCheckpoints();
    }
  }, [selectedReport]);

  // Shared date helpers
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const years = [];
  for (let y = now.getFullYear(); y >= 2020; y--) {
    years.push(y);
  }
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  // Closing inventory date
  const [asOfYear, setAsOfYear] = React.useState(now.getFullYear());
  const [asOfMonth, setAsOfMonth] = React.useState(now.getMonth());
  const [asOfDay, setAsOfDay] = React.useState(now.getDate());
  const daysInAsOfMonth = getDaysInMonth(asOfYear, asOfMonth);

  React.useEffect(() => {
    if (asOfDay > daysInAsOfMonth) setAsOfDay(daysInAsOfMonth);
  }, [asOfYear, asOfMonth, daysInAsOfMonth, asOfDay]);

  const getAsOfDate = () => new Date(asOfYear, asOfMonth, asOfDay);

  // Consumption report: start date
  const [startYear, setStartYear] = React.useState(now.getFullYear());
  const [startMonth, setStartMonth] = React.useState(now.getMonth() > 0 ? now.getMonth() - 1 : 11);
  const [startDay, setStartDay] = React.useState(1);
  const daysInStartMonth = getDaysInMonth(startYear, startMonth);

  React.useEffect(() => {
    if (startDay > daysInStartMonth) setStartDay(daysInStartMonth);
  }, [startYear, startMonth, daysInStartMonth, startDay]);

  // Consumption report: end date
  const [endYear, setEndYear] = React.useState(now.getFullYear());
  const [endMonth, setEndMonth] = React.useState(now.getMonth() > 0 ? now.getMonth() - 1 : 11);
  const lastDayDefault = getDaysInMonth(now.getFullYear(), now.getMonth() > 0 ? now.getMonth() - 1 : 11);
  const [endDay, setEndDay] = React.useState(lastDayDefault);
  const daysInEndMonth = getDaysInMonth(endYear, endMonth);

  React.useEffect(() => {
    if (endDay > daysInEndMonth) setEndDay(daysInEndMonth);
  }, [endYear, endMonth, daysInEndMonth, endDay]);

  const getStartDate = () => new Date(startYear, startMonth, startDay);
  const getEndDate = () => new Date(endYear, endMonth, endDay);
  const cancelledRef = React.useRef(false);
  const startTimeRef = React.useRef(null);
  const totalProcessedRef = React.useRef(null);
  const trackerRef = React.useRef(null);
  const [stale, setStale] = React.useState(false);
  const [runningReport, setRunningReport] = React.useState(null);

  // Check for in-progress reports when dialog opens
  React.useEffect(() => {
    if (open && store?._id) {
      storesMng.getRunningReport(store._id).then((doc) => {
        if (doc) {
          setRunningReport(doc);
        } else {
          setRunningReport(null);
        }
      }).catch(() => {});
    }
    if (!open) {
      setRunningReport(null);
    }
  }, [open, store?._id]);

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const cleanupProgress = () => {
    if (trackerRef.current) {
      trackerRef.current.cleanup();
      trackerRef.current = null;
    }
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => { cleanupProgress(); };
  }, []);

  // Helper to set up progress tracking for a report using the shared tracker utility.
  // Subscribes to the DDP publication and starts reactive tracking in one call.
  // onCompleted(progressDoc) — optional custom handler (e.g. Audit Trail's HTTP download).
  const setupProgressTracking = async (progressId, itemLabel = 'items', onCompleted) => {
    setStale(false);
    const tracker = createProgressTracker(server);
    trackerRef.current = tracker;

    await tracker.start(progressId, {
      onUpdate: (doc) => {
        if (cancelledRef.current) return;

        if (doc.total) {
          totalProcessedRef.current = { total: doc.total, processed: doc.processed || 0 };
        }

        const elapsed = startTimeRef.current ? formatDuration(Date.now() - startTimeRef.current) : '';
        const timeStr = elapsed ? ` — ${elapsed}` : '';

        const message = doc.currentItem
          ? `Processing ${doc.currentItem} (${doc.processed}/${doc.total} ${itemLabel})${timeStr}`
          : doc.status === 'started'
            ? `Starting report generation...${timeStr}`
            : `Processing... (${doc.processed || 0}/${doc.total || 0} ${itemLabel})${timeStr}`;

        const progressValue = typeof doc.progress === 'number' ? doc.progress : 0;
        setExportState({ value: 'inProgress', message, progress: progressValue });
      },
      onCompleted: (doc) => {
        if (cancelledRef.current) return;
        if (onCompleted) {
          onCompleted(doc);
          return;
        }
        const elapsed = startTimeRef.current ? formatDuration(Date.now() - startTimeRef.current) : '';
        const timeStr = elapsed ? ` — ${elapsed}` : '';
        setExportState({ value: 'inProgress', message: `Preparing download...${timeStr}`, progress: 100 });
      },
      onError: (doc) => {
        if (cancelledRef.current) return;
        setExportState({ value: 'error', message: doc.error || 'Report generation failed' });
        toast.error(doc.error || 'Report generation failed');
        cleanupProgress();
      },
      onStale: () => {
        if (!cancelledRef.current) setStale(true);
      },
      staleTimeout: 60000,
    });
  };

  // Shared with the item detail page — see stores/api/pricing.js. Implementing
  // this rule separately here is what let the export emit a literal 0.00 for
  // unpriced items while the detail page correctly showed nothing.
  const effectiveUnitRate = (i) => resolveUnitRate(i).rate;

  const mapStoreItem = (i) => ({
    'Name': i.type?.name || 'N/A',
    'Code': i.type?.code || 'N/A',
    'Quantity': i.quantity || 0,
    'Unit': i.type?.unit || 'N/A',
    'Original Price': i.type?.price || 'N/A',
    'Description': i.type?.category?.name || 'N/A',
    'Store': i.store?.name || 'N/A',
    'Status': i.isActive === false ? 'Inactive' : 'Active',
    'Location': i.location
      ? `${i.location.rack}-${i.location.shelf}-${i.location.level}`
      : 'N/A',
    'Weighted Avg Price': effectiveUnitRate(i) != null
      ? Number(effectiveUnitRate(i)).toFixed(2)
      : 'N/A',
    'Last Purchase Price': i.adjustedUnitRate?.lastInputtedRate != null && Number.isFinite(i.adjustedUnitRate.lastInputtedRate)
      ? Number(i.adjustedUnitRate.lastInputtedRate).toFixed(2)
      : 'N/A',
    'Total Value':
      i.quantity != null && effectiveUnitRate(i) != null
        ? (i.quantity * effectiveUnitRate(i)).toFixed(2)
        : 'N/A',
    'Transactions': i.transactionsCount || 0,
    'Created': i.createdAt
      ? new Date(i.createdAt).toLocaleDateString()
      : 'N/A',
    'Last Updated': i.updatedAt?.date
      ? new Date(i.updatedAt.date).toLocaleDateString()
      : 'N/A',
    'Last Updated By': i.updatedAt?.profile
      ? `${i.updatedAt.profile.firstName || ''} ${
          i.updatedAt.profile.lastName || ''
        }`.trim() || 'N/A'
      : 'N/A',
  });

  const mapStoreItems = (storeItems) => {
    if (!Array.isArray(storeItems)) {
      throw Error('store items is null');
    }
    return storeItems.map(mapStoreItem);
  };

  const exportStoreItems = async () => {
    cancelledRef.current = false;
    startTimeRef.current = Date.now();
    cleanupProgress();
    setStale(false);
    const PAGE_SIZE = 500;
    const allItems = [];

    try {
      setExportState({ value: 'inProgress', message: 'Fetching store items...', progress: 0 });

      const firstPage = await callWithTabRetry(() =>
        storesMng.getStoreItemsPage({ storeId: store._id, skip: 0, limit: PAGE_SIZE })
      );
      if (cancelledRef.current) return;

      const total = firstPage.total;
      allItems.push(...firstPage.items);

      setExportState({
        value: 'inProgress',
        message: `Fetching items... ${allItems.length}/${total}`,
        progress: Math.round((allItems.length / total) * 90),
      });

      while (allItems.length < total) {
        if (cancelledRef.current) return;

        const skip = allItems.length;
        const page = await callWithTabRetry(() =>
          storesMng.getStoreItemsPage({ storeId: store._id, skip, limit: PAGE_SIZE })
        );
        if (cancelledRef.current) return;

        allItems.push(...page.items);

        setExportState({
          value: 'inProgress',
          message: `Fetching items... ${allItems.length}/${total}`,
          progress: Math.round((allItems.length / total) * 90),
        });
      }

      let itemsToExport = allItems;
      if (!includeInactive) {
        itemsToExport = allItems.filter(i => i.isActive !== false);
      }

      setExportState({ value: 'inProgress', message: 'Generating Excel file...', progress: 92 });
      await new Promise(r => setTimeout(r, 0));

      const data = itemsToExport.map(mapStoreItem);
      const dateStr = new Date().toISOString().split('T')[0];
      exportToExcel(data, `store-items-${dateStr}.xlsx`, {
        headerRows: [
          ['Report:', 'Store Items'],
          ['Store:', store?.name || 'N/A'],
          ['Generated:', new Date().toLocaleString()],
          ['Items:', `${data.length} (${includeInactive ? 'including inactive' : 'active only'})`],
        ],
      });

      const duration = formatDuration(Date.now() - startTimeRef.current);
      setExportState({
        value: 'completed',
        message: `Export completed — ${data.length} items in ${duration}`,
        progress: 100,
      });
      toast.success(`Exported ${data.length} store items in ${duration}`);
    } catch (error) {
      console.error('Export failed:', error);
      setExportState({ value: 'error', message: error.message || 'Export failed' });
      toast.error('Failed to export store items');
    }
  };

  const exportInventoryConsumption = async () => {
    startTimeRef.current = Date.now();
    totalProcessedRef.current = null;
    setExportState({
      value: 'inProgress',
      message: 'Initializing report generation',
      progress: 0,
    });

    try {
      const start = getStartDate();
      start.setHours(0, 0, 0, 0);

      const end = getEndDate();
      end.setHours(23, 59, 59, 999);

      const progressId = Math.random().toString(36).substring(2, 15);

      // When server marks progress as 'completed', download CSV via HTTP
      await setupProgressTracking(progressId, 'items', (progressDoc) => {
        cleanupProgress();
        const rowCount = progressDoc.rowCount || 0;
        if (rowCount === 0) {
          toast.info('No data found for the selected date range');
          setExportState({ value: 'completed', message: 'No data found' });
          return;
        }
        const httpUrl = getServerHttpUrl();
        const downloadUrl = `${httpUrl}/api/report-download?id=${progressId}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `inventory-consumption-${start.toISOString().split('T')[0]}-to-${end.toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const duration = formatDuration(Date.now() - startTimeRef.current);
        setExportState({ value: 'completed', message: `${rowCount} items exported in ${duration}` });
        toast.success(`Consumption report — ${rowCount} items in ${duration}`);
      });

      // Fire-and-forget — the subscription callback handles the download
      storesMng.getStoreInventoryConsumptionReport({
        storeId: store._id,
        startDate: start,
        endDate: end,
        progressId,
      }).catch((error) => {
        cleanupProgress();
        console.error('Report generation failed:', error);
        setExportState({ value: 'error', message: 'Failed to generate report' });
        toast.error(`Failed to generate report: ${error.message || 'Unknown error'}`);
      });
    } catch (error) {
      cleanupProgress();
      console.error('Report generation failed:', error);
      setExportState({
        value: 'error',
        message: 'Failed to generate report',
      });
      toast.error(`Failed to generate report: ${error.message || 'Unknown error'}`);
    }
  };

  const handleReportSelect = (reportType) => {
    setSelectedReport(reportType);
  };

  const exportAuditTrail = async () => {
    startTimeRef.current = Date.now();
    totalProcessedRef.current = null;
    setExportState({
      value: 'inProgress',
      message: 'Initializing audit trail export',
      progress: 0,
    });

    try {
      const progressId = Math.random().toString(36).substring(2, 15);

      const start = getStartDate();
      start.setHours(0, 0, 0, 0);

      const end = getEndDate();
      end.setHours(23, 59, 59, 999);

      // When server marks progress as 'completed', download CSV via HTTP
      // This avoids sending large CSV data over DDP which can hang/timeout
      await setupProgressTracking(progressId, 'transactions', (progressDoc) => {
        cleanupProgress();
        const rowCount = progressDoc.rowCount || 0;
        if (rowCount === 0) {
          toast.info('No transactions found for the selected date range');
          setExportState({ value: 'completed', message: 'No data found' });
          return;
        }

        // Download CSV via HTTP endpoint (not DDP)
        const httpUrl = getServerHttpUrl();
        const downloadUrl = `${httpUrl}/api/report-download?id=${progressId}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `audit-trail-${start.toISOString().split('T')[0]}-to-${end.toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const duration = formatDuration(Date.now() - startTimeRef.current);
        setExportState({ value: 'completed', message: `${rowCount} transactions exported in ${duration}` });
        toast.success(`Audit trail — ${rowCount} transactions in ${duration}`);
      });

      // Fire-and-forget the method call — the subscription callback handles the download
      storesMng.exportStoreAuditTrail({
        storeId: store._id,
        startDate: start,
        endDate: end,
        progressId,
      }).catch((error) => {
        cleanupProgress();
        console.error('Audit trail export failed:', error);
        setExportState({
          value: 'error',
          message: 'Failed to export audit trail',
        });
        toast.error(`Failed to export audit trail: ${error.message || 'Unknown error'}`);
      });
    } catch (error) {
      cleanupProgress();
      console.error('Audit trail export failed:', error);
      setExportState({
        value: 'error',
        message: 'Failed to export audit trail',
      });
      toast.error(`Failed to export audit trail: ${error.message || 'Unknown error'}`);
    }
  };

  const handleGenerateReport = () => {
    // Validate date range for reports that use start/end dates
    if (selectedReport === 'inventoryConsumption' || selectedReport === 'auditTrail') {
      const start = getStartDate();
      const end = getEndDate();
      if (start > end) {
        toast.error('Start date cannot be later than end date');
        return;
      }
    }

    if (selectedReport === 'storeItems') {
      exportStoreItems();
    } else if (selectedReport === 'inventoryConsumption') {
      exportInventoryConsumption();
    } else if (selectedReport === 'closingInventory') {
      exportClosingInventory();
    } else if (selectedReport === 'auditTrail') {
      exportAuditTrail();
    }
  };

  const _onClose = () => {
    cancelledRef.current = true;
    cleanupProgress();
    setExportState({
      value: 'idle',
      message: '',
    });
    setSelectedReport(null);
    onClose();
  };

  const exportClosingInventory = async () => {
    startTimeRef.current = Date.now();
    totalProcessedRef.current = null;
    setExportState({
      value: 'inProgress',
      message: 'Initializing closing inventory report',
      progress: 0,
    });

    try {
      const progressId = Math.random().toString(36).substring(2, 15);

      const date = getAsOfDate();
      date.setHours(23, 59, 59, 999);

      // When server marks progress as 'completed', download CSV via HTTP
      await setupProgressTracking(progressId, 'items', (progressDoc) => {
        cleanupProgress();
        const rowCount = progressDoc.rowCount || 0;
        if (rowCount === 0) {
          toast.info('No data found for the selected date');
          setExportState({ value: 'completed', message: 'No data found' });
          return;
        }
        const httpUrl = getServerHttpUrl();
        const downloadUrl = `${httpUrl}/api/report-download?id=${progressId}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        const dateStr = `${asOfYear}-${String(asOfMonth + 1).padStart(2, '0')}-${String(asOfDay).padStart(2, '0')}`;
        link.download = `closing-inventory-${dateStr}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const duration = formatDuration(Date.now() - startTimeRef.current);
        let message = `${rowCount} items exported in ${duration}`;
        setExportState({ value: 'completed', message });
        toast.success(`Closing inventory — ${rowCount} items in ${duration}`);
        // Refresh checkpoints list if we saved one
        if (saveValuation) loadCheckpoints();
      });

      // Fire-and-forget — the subscription callback handles the download
      storesMng.getClosingInventoryReport({
        storeId: store._id,
        asOfDate: date,
        progressId,
        skipSavedValuation: !useSavedValuation,
        saveSnapshot: saveValuation,
      }).catch((error) => {
        cleanupProgress();
        console.error('Report generation failed:', error);
        setExportState({ value: 'error', message: 'Failed to generate report' });
        toast.error(`Failed to generate report: ${error.message || 'Unknown error'}`);
      });
    } catch (error) {
      cleanupProgress();
      console.error('Report generation failed:', error);
      setExportState({
        value: 'error',
        message: 'Failed to generate report',
      });
      toast.error(`Failed to generate report: ${error.message || 'Unknown error'}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={exportState.value !== 'inProgress' ? _onClose : undefined}
      disableBackdropClick={exportState.value === 'inProgress'}
      className={'fullScreenMobile'}
      scroll={'body'}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '18px', fontWeight: 600 }}>Generate Reports</span>
          <div
            onClick={_onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: isDark ? '#94a3b8' : '#64748b',
              fontSize: '20px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <MdClose />
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        {exportState.value === 'inProgress' ? (
          <Box style={{
            textAlign: 'center',
            padding: '32px 24px',
            margin: '8px 0',
          }}>
            <Typography variant="body1" style={{
              marginBottom: 20,
              color: theme.palette.text.primary,
            }}>
              {exportState.message}
            </Typography>
            <LinearProgress
              variant={exportState.progress != null && exportState.progress < 100 ? "determinate" : "indeterminate"}
              value={exportState.progress != null && exportState.progress < 100 ? Number(exportState.progress) : undefined}
              style={{ height: 6, borderRadius: 3 }}
            />
            {exportState.progress != null && exportState.progress < 100 && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
                {Math.round(exportState.progress)}%
              </Typography>
            )}
            {stale ? (
              <>
                <Typography variant="body2" style={{ marginTop: 12, color: isDark ? '#fbbf24' : '#d97706' }}>
                  No updates received for a while. The report may be stuck.
                </Typography>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await storesMng.clearStaleReports(store?._id);
                      setExportState({ value: 'idle', message: '' });
                      setStale(false);
                    } catch (e) {
                      console.error('Failed to clear reports:', e);
                    }
                  }}
                  style={{ marginTop: 10, textTransform: 'none', color: isDark ? '#fbbf24' : '#d97706' }}
                >
                  Clear stuck reports & retry
                </Button>
              </>
            ) : (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 12, display: 'block' }}>
                Please keep this tab open while the report generates.
              </Typography>
            )}
          </Box>
        ) : exportState.value === 'completed' ? (
          <Box style={{
            textAlign: 'center',
            padding: '32px 24px',
            margin: '8px 0',
            borderRadius: 12,
            backgroundColor: 'rgba(76, 175, 80, 0.06)',
          }}>
            <ICheck style={{
              fontSize: 56,
              color: theme.palette.success?.main || '#4caf50',
            }} />
            <Typography variant="subtitle1" style={{
              marginTop: 16,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}>
              Export Complete
            </Typography>
            <Typography variant="body2" style={{
              marginTop: 6,
              color: theme.palette.text.secondary,
            }}>
              {exportState.message}
            </Typography>
          </Box>
        ) : exportState.value === 'error' ? (
          <Box style={{
            textAlign: 'center',
            padding: '32px 24px',
            margin: '8px 0',
            borderRadius: 12,
            backgroundColor: 'rgba(244, 67, 54, 0.06)',
          }}>
            <IError style={{
              fontSize: 56,
              color: theme.palette.error?.main || '#f44336',
            }} />
            <Typography variant="subtitle1" style={{
              marginTop: 16,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}>
              Export Failed
            </Typography>
            <Typography variant="body2" style={{
              marginTop: 6,
              color: theme.palette.text.secondary,
            }}>
              {exportState.message || 'An error occurred'}
            </Typography>
            {(exportState.message || '').toLowerCase().includes('already running') && (
              <Button
                size="small"
                variant="outlined"
                onClick={async () => {
                  try {
                    await storesMng.clearStaleReports(store?._id);
                    setExportState({ value: 'idle', message: '' });
                  } catch (e) {
                    console.error('Failed to clear reports:', e);
                  }
                }}
                style={{ marginTop: 16, textTransform: 'none', borderColor: theme.palette.error?.main || '#f44336', color: theme.palette.error?.main || '#f44336' }}
              >
                Clear stuck reports & retry
              </Button>
            )}
          </Box>
        ) : (
          <>
            {runningReport && (
              <Box style={{
                padding: '12px 16px',
                marginBottom: 12,
                borderRadius: 10,
                backgroundColor: isDark ? 'rgba(251, 191, 36, 0.08)' : 'rgba(217, 119, 6, 0.06)',
                border: isDark ? '1px solid rgba(251, 191, 36, 0.2)' : '1px solid rgba(217, 119, 6, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}>
                <div>
                  <Typography variant="body2" style={{
                    fontWeight: 600,
                    color: isDark ? '#fbbf24' : '#92400e',
                    fontSize: 13,
                  }}>
                    A previous report is still running
                  </Typography>
                  <Typography variant="caption" style={{
                    color: isDark ? '#d4a017' : '#b45309',
                    display: 'block',
                    marginTop: 2,
                  }}>
                    {runningReport.progress != null ? `${Math.round(runningReport.progress)}% complete` : 'In progress'}
                    {runningReport.currentItem ? ` — ${runningReport.currentItem}` : ''}
                  </Typography>
                </div>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await storesMng.clearStaleReports(store?._id);
                      setRunningReport(null);
                    } catch (e) {
                      console.error('Failed to clear reports:', e);
                    }
                  }}
                  style={{
                    textTransform: 'none',
                    fontSize: 12,
                    flexShrink: 0,
                    backgroundColor: isDark ? 'rgba(251, 191, 36, 0.15)' : 'rgba(217, 119, 6, 0.1)',
                    color: isDark ? '#fbbf24' : '#92400e',
                  }}
                >
                  Clear & continue
                </Button>
              </Box>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { key: 'storeItems', title: 'Export All Store Items', desc: 'Current stock levels, rates, and item details' },
                { key: 'inventoryConsumption', title: 'Inventory Consumption', desc: 'Stock out totals and costs by item for a date range' },
                { key: 'closingInventory', title: 'Closing Inventory', desc: 'Inventory valuation as of a specific date' },
                { key: 'auditTrail', title: 'Audit Trail Export', desc: 'All transaction history for a date range' },
              ].map((report) => {
                const isSelected = selectedReport === report.key;
                return (
                  <div
                    key={report.key}
                    onClick={() => handleReportSelect(report.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      border: isSelected
                        ? '1.5px solid #6366f1'
                        : isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                      backgroundColor: isSelected
                        ? (isDark ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.04)')
                        : 'transparent',
                      transition: 'all 0.15s ease',
                    }}>
                    <ICSV style={{
                      flexShrink: 0,
                      fontSize: '20px',
                      color: isSelected ? '#6366f1' : (isDark ? '#94a3b8' : '#64748b'),
                    }} />
                    <div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? '#6366f1' : (isDark ? '#e2e8f0' : '#1e293b'),
                      }}>
                        {report.title}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: isDark ? '#94a3b8' : '#64748b',
                        marginTop: '2px',
                      }}>
                        {report.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedReport === 'storeItems' && (
              <Box style={{
                margin: '8px 16px 16px',
                padding: 16,
                borderRadius: 8,
                backgroundColor: theme.palette.action.hover,
              }}>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
                  Exports a full snapshot of every item in this store. Includes name, code, unit, quantity, weighted average price, last purchase price, total value, location, status, and transaction count.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeInactive}
                      onChange={(e) => setIncludeInactive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Include inactive items"
                />
              </Box>
            )}

            {selectedReport === 'closingInventory' && (
              <Box style={{
                margin: '8px 16px 16px',
                padding: 16,
                borderRadius: 8,
                backgroundColor: theme.palette.action.hover,
              }}>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
                  Calculates what the inventory looked like on a specific date by replaying transactions. Shows quantity, weighted average rate, and closing value for each item as of that date.
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  As of Date
                </Typography>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <FormControl style={{ flex: '1 1 80px' }}>
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={asOfYear}
                      onChange={(e) => setAsOfYear(e.target.value)}
                    >
                      {years.map(y => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl style={{ flex: '1.3 1 100px' }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={asOfMonth}
                      onChange={(e) => setAsOfMonth(e.target.value)}
                    >
                      {months.map((m, i) => (
                        <MenuItem key={i} value={i}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl style={{ flex: '0.8 1 60px' }}>
                    <InputLabel>Day</InputLabel>
                    <Select
                      value={asOfDay}
                      onChange={(e) => setAsOfDay(e.target.value)}
                    >
                      {Array.from({ length: daysInAsOfMonth }, (_, i) => i + 1).map(d => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <FormControlLabel
                  style={{ marginTop: 12, display: 'block' }}
                  control={
                    <Checkbox
                      checked={includeInactive}
                      onChange={(e) => setIncludeInactive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Include inactive items"
                />
                <FormControlLabel
                  style={{ display: 'block' }}
                  control={
                    <Checkbox
                      checked={saveValuation}
                      onChange={(e) => setSaveValuation(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Save valuation snapshot"
                />

                {/* Saved Valuations */}
                <div style={{
                  marginTop: 16,
                  padding: '12px 14px',
                  borderRadius: 8,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e8e8e8',
                }}>
                  <div
                    onClick={() => setValuationsExpanded(!valuationsExpanded)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      marginBottom: valuationsExpanded && checkpoints.length > 0 ? 8 : 0,
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <IBookmark style={{ fontSize: 16, color: isDark ? '#94a3b8' : '#64748b' }} />
                      <span style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: isDark ? '#cbd5e1' : '#475569',
                      }}>
                        Saved Valuations
                      </span>
                      {checkpoints.length > 0 && (
                        <span style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8' }}>
                          ({checkpoints.length})
                        </span>
                      )}
                      <span style={{
                        fontSize: 10,
                        color: isDark ? '#64748b' : '#94a3b8',
                        transition: 'transform 0.2s',
                        transform: valuationsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}>
                        ▼
                      </span>
                    </div>
                    {valuationsExpanded && checkpoints.length > 0 && (
                      <span
                        onClick={(e) => { e.stopPropagation(); handleClearCheckpoints(); }}
                        style={{
                          fontSize: 11,
                          color: isDark ? '#94a3b8' : '#94a3b8',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}>
                        Clear all
                      </span>
                    )}
                  </div>

                  {valuationsExpanded && (checkpointsLoading ? (
                    <Typography variant="body2" style={{ fontSize: 12, color: isDark ? '#64748b' : '#94a3b8' }}>
                      Loading...
                    </Typography>
                  ) : checkpoints.length === 0 ? (
                    <Typography variant="body2" style={{ fontSize: 12, color: isDark ? '#64748b' : '#94a3b8', marginTop: 4 }}>
                      No saved valuations yet. Enable "Save valuation snapshot" when generating a closing inventory report to create one.
                    </Typography>
                  ) : (() => {
                    // Determine which valuation would be used for the selected as-of date
                    const selectedAsOf = new Date(asOfYear, asOfMonth, asOfDay, 23, 59, 59, 999);
                    const activeValuationId = useSavedValuation
                      ? checkpoints
                          .filter(cp => new Date(cp.snapshotDate) <= selectedAsOf)
                          .sort((a, b) => new Date(b.snapshotDate) - new Date(a.snapshotDate))[0]?._id
                      : null;

                    return (<>
                    <FormControlLabel
                      style={{ marginBottom: 4 }}
                      control={
                        <Checkbox
                          checked={useSavedValuation}
                          onChange={(e) => setUseSavedValuation(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <span style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }}>
                          Use nearest saved valuation as starting point
                        </span>
                      }
                    />
                    {useSavedValuation && activeValuationId && (
                      <Typography variant="body2" style={{
                        fontSize: 11,
                        color: isDark ? '#a5b4fc' : '#6366f1',
                        marginBottom: 6,
                        padding: '4px 8px',
                        borderRadius: 4,
                        backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.06)',
                      }}>
                        Report will resume from the highlighted valuation instead of replaying all transactions from the start.
                      </Typography>
                    )}
                    {useSavedValuation && !activeValuationId && (
                      <Typography variant="body2" style={{
                        fontSize: 11,
                        color: isDark ? '#94a3b8' : '#64748b',
                        marginBottom: 6,
                      }}>
                        No saved valuation found on or before the selected date — report will replay all transactions.
                      </Typography>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {checkpoints.map((cp) => {
                        const cpDate = new Date(cp.snapshotDate);
                        const createdDate = cp.createdAt ? new Date(cp.createdAt) : null;
                        const isEditing = editingValuationId === cp._id;
                        const isActive = cp._id === activeValuationId;
                        return (
                          <div
                            key={cp._id}
                            style={{
                              padding: '8px 10px',
                              borderRadius: 6,
                              backgroundColor: isActive
                                ? (isDark ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.06)')
                                : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                              border: isActive ? (isDark ? '1px solid rgba(99,102,241,0.6)' : '1px solid rgba(99,102,241,0.3)') : '1px solid transparent',
                              transition: 'all 0.15s',
                            }}>
                            {/* Top row: date + actions */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                                <span style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: isDark ? '#e2e8f0' : '#1e293b',
                                }}>
                                  {cpDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                </span>
                                <span style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8' }}>
                                  · {cp.itemCount} items
                                </span>
                                {isActive && (
                                  <span style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: isDark ? '#a5b4fc' : '#6366f1',
                                    backgroundColor: isDark ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.1)',
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                  }}>
                                    will use
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                                {[
                                  { icon: <IDownload style={{ fontSize: 13 }} />, title: 'Download', onClick: () => handleExportCheckpoint(cp._id), hoverBg: 'rgba(99,102,241,0.1)', hoverColor: '#6366f1' },
                                  { icon: <span style={{ fontSize: 11, fontWeight: 600 }}>✎</span>, title: 'Edit', onClick: () => { if (!isEditing) { editNameRef.current = cp.name || ''; editNoteRef.current = cp.note || ''; } setEditingValuationId(isEditing ? null : cp._id); }, hoverBg: 'rgba(99,102,241,0.1)', hoverColor: '#6366f1' },
                                  { icon: <IDelete style={{ fontSize: 13 }} />, title: 'Delete', onClick: () => handleDeleteCheckpoint(cp._id), hoverBg: 'rgba(244,67,54,0.1)', hoverColor: '#f44336' },
                                ].map((btn, idx) => (
                                  <div
                                    key={idx}
                                    onClick={btn.onClick}
                                    title={btn.title}
                                    style={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      width: 26, height: 26, borderRadius: 5, cursor: 'pointer',
                                      color: isDark ? '#64748b' : '#94a3b8', transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? btn.hoverBg : btn.hoverBg; e.currentTarget.style.color = btn.hoverColor; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = isDark ? '#64748b' : '#94a3b8'; }}>
                                    {btn.icon}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Meta: who created, when */}
                            <div style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8', marginTop: 2 }}>
                              {cp.createdBy?.name && <span>by {cp.createdBy.name}</span>}
                              {createdDate && <span>{cp.createdBy?.name ? ' · ' : ''}saved {createdDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} {createdDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>}
                            </div>

                            {/* Note */}
                            {cp.note && !isEditing && (
                              <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', marginTop: 4, fontStyle: 'italic' }}>
                                {cp.note}
                              </div>
                            )}

                            {/* Edit form — just a note */}
                            {isEditing && (
                              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <TextField
                                  size="small"
                                  label="Note"
                                  placeholder="Optional note about this valuation"
                                  defaultValue={cp.note || ''}
                                  onChange={(e) => { editNoteRef.current = e.target.value; }}
                                  fullWidth
                                  multiline
                                  rows={2}
                                  InputProps={{ style: { fontSize: 13 } }}
                                  InputLabelProps={{ style: { fontSize: 12 } }}
                                />
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                  <Button
                                    size="small"
                                    onClick={() => setEditingValuationId(null)}
                                    style={{ textTransform: 'none', fontSize: 12 }}>
                                    Cancel
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={async () => {
                                      await handleUpdateCheckpoint(cp._id, null, editNoteRef.current);
                                      setEditingValuationId(null);
                                    }}
                                    style={{ textTransform: 'none', fontSize: 12, borderRadius: 6 }}>
                                    Save
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    </>);
                  })())}
                </div>
              </Box>
            )}

            {(selectedReport === 'inventoryConsumption' || selectedReport === 'auditTrail') && (
              <Box style={{
                margin: '8px 16px 16px',
                padding: 16,
                borderRadius: 8,
                backgroundColor: theme.palette.action.hover,
              }}>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
                  {selectedReport === 'inventoryConsumption'
                    ? 'Summarizes all stock-out activity for the selected period. Shows total consumed quantity, total value, weighted average rate, and transaction count per item.'
                    : 'Exports every individual transaction (in, out, transfer, modify, reverse) for the selected period. Includes date, time, item, quantity, rate, value, user, notes, invoice number, and supplier.'}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  Start Date
                </Typography>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <FormControl style={{ flex: '1 1 80px' }}>
                    <InputLabel>Year</InputLabel>
                    <Select value={startYear} onChange={(e) => setStartYear(e.target.value)}>
                      {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl style={{ flex: '1.3 1 100px' }}>
                    <InputLabel>Month</InputLabel>
                    <Select value={startMonth} onChange={(e) => setStartMonth(e.target.value)}>
                      {months.map((m, i) => <MenuItem key={i} value={i}>{m}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl style={{ flex: '0.8 1 60px' }}>
                    <InputLabel>Day</InputLabel>
                    <Select value={startDay} onChange={(e) => setStartDay(e.target.value)}>
                      {Array.from({ length: daysInStartMonth }, (_, i) => i + 1).map(d => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  End Date
                </Typography>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <FormControl style={{ flex: '1 1 80px' }}>
                    <InputLabel>Year</InputLabel>
                    <Select value={endYear} onChange={(e) => setEndYear(e.target.value)}>
                      {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl style={{ flex: '1.3 1 100px' }}>
                    <InputLabel>Month</InputLabel>
                    <Select value={endMonth} onChange={(e) => setEndMonth(e.target.value)}>
                      {months.map((m, i) => <MenuItem key={i} value={i}>{m}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl style={{ flex: '0.8 1 60px' }}>
                    <InputLabel>Day</InputLabel>
                    <Select value={endDay} onChange={(e) => setEndDay(e.target.value)}>
                      {Array.from({ length: daysInEndMonth }, (_, i) => i + 1).map(d => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {selectedReport && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '24px',
                paddingBottom: '16px',
              }}>
                <Button
                  onClick={handleGenerateReport}
                  color="primary"
                  variant="contained"
                  style={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, padding: '8px 24px' }}>
                  {selectedReport === 'storeItems' ? 'Export' :
                   selectedReport === 'auditTrail' ? 'Export Audit Trail' :
                   'Generate Report'}
                </Button>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: '8px',
              marginTop: selectedReport ? '0' : '16px',
            }}>
              <Button onClick={_onClose} style={{ textTransform: 'none', color: isDark ? '#94a3b8' : '#64748b' }}>
                Close
              </Button>
            </div>
          </>
        )}

        {exportState.value === 'inProgress' && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', paddingBottom: '8px' }}>
            <Button onClick={_onClose} color="secondary" style={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </div>
        )}

        {(exportState.value === 'completed' || exportState.value === 'error') && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px', paddingBottom: '8px' }}>
            <Button onClick={() => { setExportState({ value: 'idle', message: '' }); }} style={{ textTransform: 'none', color: isDark ? '#94a3b8' : '#64748b' }}>
              Back
            </Button>
            <Button onClick={_onClose} color="primary" variant="contained" style={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
              Done
            </Button>
          </div>
        )}
      </DialogContent>

      {/* Confirmation dialog */}
      {confirmAction && (
        <Dialog open onClose={() => setConfirmAction(null)} maxWidth="xs" fullWidth>
          <DialogTitle style={{ fontSize: 16, fontWeight: 600 }}>Confirm</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{confirmAction.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmAction(null)} style={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const action = confirmAction.onConfirm;
                setConfirmAction(null);
                await action();
              }}
              color="secondary"
              variant="contained"
              style={{ textTransform: 'none', borderRadius: 6 }}>
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
}

GenerateReportsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  store: PropTypes.object,
};

export default GenerateReportsDialog;
